import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function FestivalActivation() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/festival/checkout/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        // Iter316 — Stop polling on any terminal state. `failed` means
        // the backend saw Stripe close the session as expired / unpaid;
        // `paid` means we've fulfilled. Anything else keeps polling up
        // to the 60-try (~3-minute) cap.
        const terminal = data.payment_status === "paid"
          || data.payment_status === "failed"
          || data.payment_status === "error";
        if (!terminal && tries < 60) {
          setTimeout(() => setTries((t) => t + 1), 3000);
        }
      } catch (e) {
        if (!cancelled) setStatus({ payment_status: "error", detail: e?.response?.data?.detail });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, tries]);

  const paid = status.payment_status === "paid";
  const errored = status.payment_status === "error" || status.payment_status === "failed";
  const targetId = id === "basket" ? status.festival_id : id;

  useEffect(() => {
    if (!paid || id !== "basket" || !status.festival_id) return;
    try { window.localStorage.removeItem("rl_festival_basket_v1"); } catch {}
    navigate(`/festival/${status.festival_id}`, { replace: true });
  }, [paid, id, status.festival_id, navigate]);

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-16 text-center" data-testid="festival-activation">
      {paid ? (
        <>
          <CheckCircle2 className="w-16 h-16 text-[#CCFF00] mx-auto mb-4" />
          <div className="label-mono text-[#CCFF00] mb-1">PAYMENT CONFIRMED</div>
          <h1 className="h-display text-3xl mb-3">You're unlocked</h1>
          <p className="text-sm text-[#A3A3A3] mb-6">
            Your festival is now live. Access is valid through the event window you set.
          </p>
          <button
            type="button"
            onClick={() => navigate(targetId ? `/festival/${targetId}` : "/festival/mine")}
            data-testid="festival-activation-back"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-bold"
            style={{ background: "#CCFF00", color: "#0A0A0A" }}
          >
            Open festival
          </button>
        </>
      ) : errored ? (
        <>
          <AlertTriangle className="w-16 h-16 text-[#FF4500] mx-auto mb-4" />
          <div className="label-mono text-[#FF4500] mb-1">PAYMENT FAILED</div>
          <h1 className="h-display text-3xl mb-3">We couldn't confirm</h1>
          <p className="text-sm text-[#A3A3A3] mb-6">
            {status.detail || "Try again from the festival page. Nothing was charged if no confirmation arrived."}
          </p>
          <button
            type="button"
            onClick={() => navigate(id === "basket" ? "/festival?open_basket=1" : `/festival/${id}`)}
            data-testid="festival-activation-retry"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white"
          >
            Back to festival
          </button>
        </>
      ) : (
        <>
          <Loader2 className="w-16 h-16 text-[#CCFF00] mx-auto mb-4 animate-spin" />
          <div className="label-mono text-[#CCFF00] mb-1">CONFIRMING PAYMENT</div>
          <h1 className="h-display text-3xl mb-3">One moment…</h1>
          <p className="text-sm text-[#A3A3A3]">
            Stripe is confirming your event pass. This usually takes a few seconds.
          </p>
        </>
      )}
    </div>
  );
}
